import React, { useEffect, useMemo, useState, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import SideBar from "../components/common/SideBar";
import {
    GetMyPastQualificationService,
    GetMyQualificationService,
    GetShortCourseQualification,
} from "../services/QualificationService";
import SkeletonQaulification from "../loaders/SkeletonQualification";
import QualificationBox from "../components/course/QualificationBox";
import { getCurrentTimeHomePage } from "../components/common/Helper";
import { UpdateStudentTimeZone } from "../services/CalendarService";
import PastQualificationBox from "../components/course/PastQualificationBox";
import Header from "../components/common/Header";
import { useSelector } from "react-redux";
import { clearIsDownloadAssessment } from "../store/actions";
import { useMyCoursesViewStore } from "../store/myCoursesViewStore";
import { getResponseRows, getResponseTotal } from "../utils/apiResponse";

/**
 * Base React Query cache key for the My Courses page.
 *
 * Each query adds its data type, course period, page, and limit to this base
 * key so React Query can cache/refetch each request separately.
 */
const MY_COURSES_QUERY_KEY = ["mycourses"];

/**
 * Returns short course rows filtered by their current/past flag.
 */
const getShortCourseRows = (response, isPast) =>
    getResponseRows(response).filter(
        (item) => Boolean(item?.isPast) === isPast,
    );

const MyCourses = () => {
    const [qualUnreadMessage, setQualUnreadMessage] = useState(0);
    const [shortUnreadMessage, setShortUnreadMessage] = useState(10);
    const [, setError] = useState(null);
    const chatNotification = useSelector((state) => state.chatNotification);

    /**
     * Selects `isPast` from the My Courses Zustand store.
     *
     * Zustand provides the full store state to this selector callback. Returning
     * `state.isPast` subscribes this component only to changes for that field.
     */
    const isPast = useMyCoursesViewStore((state) => state.isPast);
    const showQual = useMyCoursesViewStore((state) => state.showQual);
    const limit = useMyCoursesViewStore((state) => state.limit);
    const pastLimit = useMyCoursesViewStore((state) => state.pastLimit);
    const showPast = useMyCoursesViewStore((state) => state.showPast);
    const page = useMyCoursesViewStore((state) => state.page);
    const pagePast = useMyCoursesViewStore((state) => state.pagePast);
    const setShowQual = useMyCoursesViewStore((state) => state.setShowQual);
    const setShowPast = useMyCoursesViewStore((state) => state.setShowPast);
    const selectQualifications = useMyCoursesViewStore(
        (state) => state.selectQualifications,
    );
    const selectShortCourses = useMyCoursesViewStore(
        (state) => state.selectShortCourses,
    );
    const showCompletedCourses = useMyCoursesViewStore(
        (state) => state.showCompletedCourses,
    );

    /**
     * Loads the learner's current qualifications.
     *
     * The page and limit are included in the query key so React Query keeps a
     * separate cache entry for each paged request.
     */
    const currentQualificationsQuery = useQuery(
        [...MY_COURSES_QUERY_KEY, "qualifications", "current", page, limit],
        () => GetMyQualificationService({ isPast: 0, limit, pageNo: page }),
        {
            keepPreviousData: true,
            onError: (queryError) => {
                console.error(queryError);
                setError("Failed to load qualifications.");
            },
            onSuccess: () => {
                setError(null);
            },
        },
    );

    const hasShortCoursesFromQualifications =
        currentQualificationsQuery.data?.data?.isShortCoursesExists > 0;

    /**
     * Loads the learner's current short courses.
     *
     * This query is disabled while the Qualifications tab is active unless the
     * qualifications response confirms that short courses exist.
     */
    const currentShortCoursesQuery = useQuery(
        [...MY_COURSES_QUERY_KEY, "short-courses", "current", page, limit],
        () => GetShortCourseQualification({ isPast: 0, limit, pageNo: page }),
        {
            enabled: !showQual || hasShortCoursesFromQualifications,
            keepPreviousData: true,
            onError: (queryError) => {
                console.error("Error fetching short courses: ", queryError);
                setError("Failed to load short courses.");
            },
            onSuccess: () => {
                setError(null);
            },
        },
    );

    /**
     * Loads completed qualifications only when the completed section is open
     * and the Qualifications tab is active.
     */
    const pastQualificationsQuery = useQuery(
        [
            ...MY_COURSES_QUERY_KEY,
            "qualifications",
            "past",
            pagePast,
            pastLimit,
        ],
        () =>
            GetMyPastQualificationService({
                isPast: 1,
                limit: pastLimit,
                pageNo: pagePast,
            }),
        {
            enabled: isPast === 1 && showQual,
            keepPreviousData: true,
            onError: (queryError) => {
                console.error(queryError);
                setError("Failed to load past qualifications.");
            },
            onSuccess: () => {
                setError(null);
            },
        },
    );

    /**
     * Loads completed short courses only when the completed section is open and
     * the Short Courses tab is active.
     */
    const pastShortCoursesQuery = useQuery(
        [...MY_COURSES_QUERY_KEY, "short-courses", "past", pagePast, pastLimit],
        () =>
            GetShortCourseQualification({
                isPast: 1,
                limit: pastLimit,
                pageNo: pagePast,
            }),
        {
            enabled: isPast === 1 && !showQual,
            keepPreviousData: true,
            onError: (queryError) => {
                console.error(
                    "Error fetching past short courses: ",
                    queryError,
                );
                setError("Failed to load past short courses.");
            },
            onSuccess: () => {
                setError(null);
            },
        },
    );

    /**
     * Normalizes the current qualifications response into the row array used by
     * the course card component.
     */
    const qualifications = useMemo(
        () => getResponseRows(currentQualificationsQuery.data),
        [currentQualificationsQuery.data],
    );

    /**
     * Normalizes the current short-course response and keeps only active rows.
     */
    const shortCourse = useMemo(
        () => getShortCourseRows(currentShortCoursesQuery.data, false),
        [currentShortCoursesQuery.data],
    );

    /**
     * Normalizes the completed qualifications response into the row array used
     * by the completed course card component.
     */
    const pastQualification = useMemo(
        () => getResponseRows(pastQualificationsQuery.data),
        [pastQualificationsQuery.data],
    );

    /**
     * Normalizes the completed short-course response and keeps only past rows.
     */
    const pastShortCourse = useMemo(
        () => getShortCourseRows(pastShortCoursesQuery.data, true),
        [pastShortCoursesQuery.data],
    );

    /**
     * Derives tab visibility, active rows, and loading flags from React Query.
     *
     * Keeping this derived from query state avoids duplicating fetched course
     * rows in component state and prevents stale lists when switching tabs.
     */
    const hasQualificationMetadata = Boolean(
        currentQualificationsQuery.dataUpdatedAt,
    );
    const hasShortCourseResults =
        hasShortCoursesFromQualifications ||
        shortCourse.length > 0 ||
        pastShortCourse.length > 0 ||
        getResponseTotal(currentShortCoursesQuery.data) > 0 ||
        getResponseTotal(pastShortCoursesQuery.data) > 0;
    const showQualification =
        qualifications.length > 0 ||
        pastQualification.length > 0 ||
        currentQualificationsQuery.data?.data?.isExistsPastQualifcation > 0 ||
        (showQual && currentQualificationsQuery.isLoading);
    const showShortCourse =
        hasShortCourseResults ||
        (!showQual && currentShortCoursesQuery.isLoading);
    const showTabs =
        showQualification ||
        showShortCourse ||
        hasQualificationMetadata ||
        currentQualificationsQuery.isLoading ||
        currentShortCoursesQuery.isLoading;
    const totalSCourses =
        getResponseTotal(currentShortCoursesQuery.data) ||
        getResponseTotal(pastShortCoursesQuery.data);
    const activeCurrentQuery = showQual
        ? currentQualificationsQuery
        : currentShortCoursesQuery;
    const activePastQuery = showQual
        ? pastQualificationsQuery
        : pastShortCoursesQuery;
    const activeCurrentRows = showQual ? qualifications : shortCourse;
    const activePastRows = showQual ? pastQualification : pastShortCourse;
    const hasActiveCurrentRows = activeCurrentRows.length > 0;
    const hasActivePastRows = activePastRows.length > 0;
    const loading =
        activeCurrentQuery.isLoading ||
        (activeCurrentQuery.isFetching && !hasActiveCurrentRows);
    const loadingPast =
        isPast === 1 &&
        (activePastQuery.isLoading ||
            (activePastQuery.isFetching && !hasActivePastRows));

    useEffect(() => {
        clearIsDownloadAssessment();
    }, []);

    useEffect(() => {
        UpdateStudentTimeZone({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }).catch(() => {});
    }, []);

    useEffect(() => {
        // DO NOT REMOVE THIS CODE
        setShowPast(false);
        const timer = setTimeout(() => {
            setShowPast(true);
        }, 200);

        return () => {
            clearTimeout(timer);
        };
    }, [setShowPast, showQual]);

    useEffect(() => {
        if (
            showQual &&
            currentQualificationsQuery.isSuccess &&
            !showQualification &&
            showShortCourse
        ) {
            setShowQual(false);
        }
    }, [
        currentQualificationsQuery.isSuccess,
        setShowQual,
        showQual,
        showQualification,
        showShortCourse,
    ]);

    useEffect(() => {
        if (
            !showQual &&
            currentQualificationsQuery.isSuccess &&
            currentShortCoursesQuery.isSuccess &&
            !hasShortCourseResults &&
            showQualification
        ) {
            setShowQual(true);
        }
    }, [
        currentQualificationsQuery.isSuccess,
        currentShortCoursesQuery.isSuccess,
        hasShortCourseResults,
        setShowQual,
        showQual,
        showQualification,
    ]);

    // useEffect(() => {
    //   window.addEventListener("scroll", handleScroll);
    //   return () => window.removeEventListener("scroll", handleScroll);
    // }, [loading, qualifications.length, total,totalPast,page,pagePast]);

    useEffect(() => {
        if (chatNotification?.chatnotificationcount) {
            setQualUnreadMessage(
                chatNotification.chatnotificationcount.qualChatcount,
            );
            setShortUnreadMessage(
                chatNotification.chatnotificationcount.shortChatcount,
            );
        }
    }, [chatNotification.chatnotificationcount]);

    return (
        <>
            <Header />
            <div className="content-wrapper d-flex flex-wrap">
                <SideBar />
                <div className="sidebar-right">
                    <div className="mycourses-list-wrap mycourses-edit tab-sec">
                        <div className="my-tickets-heading-blk flex-direction mb-4">
                            <h4
                                className={`text-left-align landing-heading heading_color_dynamic my-courses-title ${!showTabs && "hideTab__courses"}`}
                            >
                                My <span> Courses</span>
                            </h4>
                            <div className="text-md-right">
                                <p className=""></p>
                            </div>
                            <div className="lecture-tabs-wrap lecture-time-box">
                                <ul
                                    className="nav nav-pills"
                                    id="pills-tab"
                                    role="tablist"
                                >
                                    {showTabs && (
                                        <>
                                            {showQualification && (
                                                <li
                                                    className={`nav-item ${showQual ? "active" : ""}`}
                                                    title="Qualifications"
                                                >
                                                    <a
                                                        id="pills-Posts-tab"
                                                        data-toggle="pill"
                                                        href="#pills-Posts"
                                                        role="tab"
                                                        aria-controls="pills-Posts"
                                                        aria-selected={
                                                            showQual
                                                                ? "true"
                                                                : "false"
                                                        }
                                                        className={`nav-link ${showQual ? "active" : ""}`}
                                                        onClick={
                                                            selectQualifications
                                                        }
                                                    >
                                                        <i
                                                            className="far fa-graduation-cap"
                                                            aria-hidden="true"
                                                        ></i>
                                                        Qualifications
                                                        {qualUnreadMessage >
                                                            0 && (
                                                            <div
                                                                className="nav-fa-comments my-crs-head"
                                                                title="You have unread messages"
                                                            >
                                                                <small className="notify-count">
                                                                    {qualUnreadMessage <
                                                                    10
                                                                        ? qualUnreadMessage
                                                                        : "9+"}
                                                                </small>
                                                                <i className="fal fa-comments"></i>
                                                            </div>
                                                        )}
                                                    </a>
                                                </li>
                                            )}

                                            {showShortCourse && (
                                                <li
                                                    className={`nav-item ${!showQual ? "active" : ""}`}
                                                    title="Short Courses"
                                                >
                                                    <a
                                                        className={`nav-link ${!showQual ? "active" : ""}`}
                                                        id="pills-Overview-tab"
                                                        data-toggle="pill"
                                                        href="#pills-Overview"
                                                        role="tab"
                                                        aria-controls="pillsOverview"
                                                        aria-selected={
                                                            !showQual
                                                                ? "true"
                                                                : "false"
                                                        }
                                                        onClick={
                                                            selectShortCourses
                                                        }
                                                    >
                                                        <i className="fal fa-award"></i>
                                                        Short Courses
                                                        {shortUnreadMessage >
                                                            0 && (
                                                            <div
                                                                className="nav-fa-comments my-crs-head"
                                                                title="You have unread messages"
                                                            >
                                                                <small className="notify-count">
                                                                    {shortUnreadMessage <
                                                                    10
                                                                        ? shortUnreadMessage
                                                                        : "9+"}
                                                                </small>
                                                                <i className="fal fa-comments"></i>
                                                            </div>
                                                        )}
                                                    </a>
                                                </li>
                                            )}
                                        </>
                                    )}
                                </ul>
                                <p className="date-time">
                                    {" "}
                                    {getCurrentTimeHomePage(
                                        Date().toLocaleString(),
                                    )}
                                </p>
                            </div>
                        </div>
                        <div className="my-courses-list-wrap">
                            {hasActiveCurrentRows && (
                                <QualificationBox
                                    showQualification={showQual}
                                    qualification={activeCurrentRows}
                                    showProgram={true}
                                    state={{
                                        isPast,
                                        qualifications,
                                        pastQualification,
                                        shortCourse,
                                        loading,
                                        showQual,
                                        totalSCourses,
                                        showTabs,
                                    }}
                                />
                            )}
                            {loading && <SkeletonQaulification />}
                        </div>
                        <>
                            <button
                                aria-expanded={isPast === 1 ? "true" : "false"}
                                className={`${!showQualification && !showShortCourse ? "hide" : ""} ${isPast === 1 ? "" : "collapsed"} ${showQual ? "hide-completed-program" : "hide-completed-course"}`}
                                onClick={showCompletedCourses}
                            ></button>
                            {showPast && (
                                <div
                                    className={`collapse ${isPast === 1 ? "show" : ""}`}
                                    id="show-hide-past-qualification"
                                >
                                    {!loadingPast ? (
                                        hasActivePastRows ? (
                                            <div className="my-courses-list-wrap">
                                                <PastQualificationBox
                                                    state={{
                                                        isPast,
                                                        pastQualification,
                                                        pastShortCourse,
                                                        loading,
                                                        showQual,
                                                        totalSCourses,
                                                        showTabs,
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-100 text-center">
                                                No past{" "}
                                                {showQual
                                                    ? "qualifications"
                                                    : "short courses"}
                                            </div>
                                        )
                                    ) : null}
                                    {loadingPast && <SkeletonQaulification />}
                                </div>
                            )}
                        </>
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(MyCourses);
